import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { z } from "zod";
import type { ChatApi } from "@/lib/types/ai-chat";

export interface QueuedChatMessage {
  id: string;
  text: string;
  fileContentIds: string[];
  status: "processing" | "sending" | "error" | "delivered";
  fileReferences?: {
    fileContentId: string;
    fileName: string;
    processingId?: string;
  }[];
  error?: string;
  matterId?: string;
  blockedRunId?: string;
  /** Retain the exact destination after an uncertain HTTP outcome. */
  destination?: { runId?: string; conversationId?: string };
}
interface Options {
  scopeKey: string;
  threadId?: string;
  matterId?: string;
  api: Pick<ChatApi, "getThread" | "startRun" | "steerRun">;
  onDelivered(result: { conversationId: string; runId?: string }): void;
  onError?(error: Error): void;
  onBackgroundDelivered?(): void;
  backgroundOnly?: boolean;
  confirmedRequestIds?: string[];
}
const prefix = "lawworks-chat-message-queue:";
const deliveries = new Set<string>();
function storageKey(scope: string, thread?: string, matterId?: string) {
  return `${prefix}${encodeURIComponent(scope)}:${thread ?? `new:${encodeURIComponent(matterId ?? "")}`}`;
}
const queuedMessagesSchema = z.array(
  z
    .object({
      id: z.string(),
      text: z.string(),
      fileContentIds: z.array(z.string()).max(50),
      status: z.enum(["processing", "sending", "error", "delivered"]),
      fileReferences: z
        .array(
          z.object({
            fileContentId: z.string(),
            fileName: z.string(),
            processingId: z.string().optional(),
          }),
        )
        .max(50)
        .optional(),
      error: z.string().optional(),
      matterId: z.string().optional(),
      blockedRunId: z.string().optional(),
      destination: z
        .object({
          runId: z.string().optional(),
          conversationId: z.string().optional(),
        })
        .optional(),
    })
    .refine(
      (value) =>
        value.fileReferences?.every((file) =>
          value.fileContentIds.includes(file.fileContentId),
        ) ?? true,
    ),
);
function readQueue(key: string): QueuedChatMessage[] {
  const raw = sessionStorage.getItem(key);
  if (!raw) return [];
  const values: unknown = JSON.parse(raw);
  const parsed = queuedMessagesSchema.safeParse(values);
  if (!parsed.success) {
    throw new Error(
      "Your queued messages could not be loaded. Please reload this page.",
    );
  }
  return parsed.data;
}
function saveQueue(key: string, messages: QueuedChatMessage[]) {
  if (messages.length) sessionStorage.setItem(key, JSON.stringify(messages));
  else sessionStorage.removeItem(key);
  window.dispatchEvent(
    new CustomEvent("lawworks-chat-queue-change", { detail: key }),
  );
}
function httpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } } | undefined)?.response
    ?.status;
}

/** A per-tab outbox. Saved files can reach inference independently of processing. */
export function useAiChatMessageQueue(options: Options) {
  const key = storageKey(options.scopeKey, options.threadId, options.matterId);
  const current = useRef(options);
  current.current = options;
  const trigger = useRef<() => void>(() => {});
  const keyRef = useRef(key);
  keyRef.current = key;
  const [snapshot, setSnapshot] = useState<{
    key: string;
    messages: QueuedChatMessage[];
  }>({ key, messages: [] });
  const publish = useCallback((queueKey: string) => {
    if (keyRef.current !== queueKey) return;
    try {
      setSnapshot({ key: queueKey, messages: readQueue(queueKey) });
    } catch {
      current.current.onError?.(
        new Error(
          "Your browser could not load queued messages. Please reload this page.",
        ),
      );
    }
  }, []);

  useEffect(() => {
    const changed = (event: Event) => {
      publish((event as CustomEvent<string>).detail);
    };
    const delivered = (event: Event) => {
      const { scope, sourceKey, result } = (
        event as CustomEvent<{
          scope: string;
          sourceKey: string;
          result: { conversationId: string; runId?: string };
        }>
      ).detail;
      if (!scope || scope !== current.current.scopeKey) return;
      if (!current.current.backgroundOnly && sourceKey === keyRef.current)
        current.current.onDelivered(result);
      else current.current.onBackgroundDelivered?.();
    };
    window.addEventListener("lawworks-chat-queue-change", changed);
    window.addEventListener("lawworks-chat-queue-delivered", delivered);
    return () => {
      window.removeEventListener("lawworks-chat-queue-change", changed);
      window.removeEventListener("lawworks-chat-queue-delivered", delivered);
    };
  }, [publish]);

  useEffect(() => {
    let stopped = false;
    const scope = options.scopeKey;
    const scopePrefix = `${prefix}${encodeURIComponent(scope)}:`;
    const api = options.api;
    const stillHere = () => !stopped && current.current.scopeKey === scope;
    const pumpQueue = async (key: string) => {
      const suffix = key.slice(scopePrefix.length);
      const conversationId =
        suffix === "new" || suffix.startsWith("new:") ? undefined : suffix;
      if (!scope || !stillHere() || deliveries.has(key)) return;
      deliveries.add(key);
      let entry: QueuedChatMessage | undefined;
      try {
        const queue = readQueue(key);
        publish(key);
        const selectedEntry = queue.find(
          (message) => message.status !== "delivered",
        );
        entry = selectedEntry;
        if (!selectedEntry || selectedEntry.status === "error") return;
        if (!stillHere()) return;
        // Do not dispatch a message removed from the queue.
        if (
          readQueue(key).find((message) => message.status !== "delivered")
            ?.id !== selectedEntry.id
        )
          return;
        for (let attempt = 0; attempt < 2; attempt++) {
          if (!selectedEntry.destination) {
            const thread = conversationId
              ? await api.getThread(conversationId)
              : undefined;
            if (!stillHere()) return;
            if (thread?.readOnly)
              throw new Error("This conversation is read-only.");
            if (
              selectedEntry.blockedRunId &&
              thread?.activeRunId === selectedEntry.blockedRunId
            )
              return;
            selectedEntry.blockedRunId = undefined;
            selectedEntry.destination = {
              conversationId,
              runId: thread?.activeRunId ?? undefined,
            };
          }
          selectedEntry.status = "sending";
          const latest = readQueue(key);
          if (!latest.some((message) => message.id === selectedEntry.id))
            return;
          saveQueue(
            key,
            latest.map((message) =>
              message.id === selectedEntry.id ? selectedEntry : message,
            ),
          );
          publish(key);
          let result: { conversationId: string; runId?: string };
          try {
            const input = {
              clientRequestId: selectedEntry.id,
              userMessage: selectedEntry.text,
              fileContentIds: selectedEntry.fileContentIds,
            };
            if (selectedEntry.destination.runId) {
              if (!selectedEntry.destination.conversationId) {
                throw new Error(
                  "The saved message destination is unavailable.",
                );
              }
              await api.steerRun(selectedEntry.destination.runId, input);
              result = {
                conversationId: selectedEntry.destination.conversationId,
                runId: selectedEntry.destination.runId,
              };
            } else {
              const started = await api.startRun({
                ...input,
                conversationId: selectedEntry.destination.conversationId,
                matterId: selectedEntry.matterId,
              });
              result = {
                conversationId: started.conversationId,
                runId: started.run.id,
              };
            }
          } catch (error) {
            const code = (
              error as
                | { response?: { data?: { error?: { code?: string } } } }
                | null
                | undefined
            )?.response?.data?.error?.code;
            if (
              httpStatus(error) === 409 &&
              ["run_not_accepting", "active_run"].includes(code ?? "")
            ) {
              selectedEntry.blockedRunId =
                code === "run_not_accepting"
                  ? selectedEntry.destination.runId
                  : undefined;
              selectedEntry.destination = undefined;
              selectedEntry.status = "processing";
              saveQueue(
                key,
                readQueue(key).map((saved) =>
                  saved.id === selectedEntry.id ? selectedEntry : saved,
                ),
              );
              publish(key);
              return;
            }
            // Only an explicit rejection makes it safe to choose another run.
            if (httpStatus(error) === 409 && attempt === 0 && stillHere()) {
              selectedEntry.destination = undefined;
              continue;
            }
            throw error;
          }
          selectedEntry.status = "delivered";
          const remaining = readQueue(key).map((message) =>
            message.id === selectedEntry.id ? selectedEntry : message,
          );
          const deliveredKey = storageKey(scope, result.conversationId);
          if (deliveredKey !== key) {
            // Older saved queues may contain more than one matter. Only carry
            // messages from the created conversation's matter into its queue.
            const belongs = (message: QueuedChatMessage) =>
              conversationId !== undefined ||
              message.matterId === selectedEntry.matterId;
            saveQueue(deliveredKey, [
              ...readQueue(deliveredKey),
              ...remaining.filter(belongs),
            ]);
            saveQueue(
              key,
              remaining.filter((message) => !belongs(message)),
            );
          } else saveQueue(key, remaining);
          publish(key);
          window.dispatchEvent(
            new CustomEvent("lawworks-chat-queue-delivered", {
              detail: { scope, sourceKey: key, result },
            }),
          );
          return;
        }
      } catch (cause) {
        const message =
          cause instanceof Error && cause.message.startsWith("A file could not")
            ? cause.message
            : "The message could not be sent. Please retry.";
        try {
          if (entry) {
            const failedEntry = entry;
            saveQueue(
              key,
              readQueue(key).map((saved) =>
                saved.id === failedEntry.id
                  ? { ...failedEntry, status: "error", error: message }
                  : saved,
              ),
            );
            publish(key);
          }
        } catch {
          // Storage may become unavailable. Keep the visible draft actionable.
          if (stillHere() && keyRef.current === key && entry)
            setSnapshot({
              key,
              messages: [
                {
                  ...entry,
                  status: "error",
                  error:
                    "Your browser could not save this message. Please keep this page open and retry.",
                },
              ],
            });
        }
        if (stillHere() && keyRef.current === key)
          current.current.onError?.(new Error(message));
      } finally {
        deliveries.delete(key);
      }
    };
    const pump = async () => {
      if (!scope || !stillHere()) return;
      try {
        const keys = Array.from({ length: sessionStorage.length }, (_, i) =>
          sessionStorage.key(i),
        ).filter((value): value is string =>
          Boolean(value?.startsWith(scopePrefix)),
        );
        await Promise.all(keys.map(pumpQueue));
      } catch {
        if (stillHere())
          current.current.onError?.(
            new Error(
              "Your browser could not load queued messages. Please reload this page.",
            ),
          );
      }
    };
    trigger.current = () => void pump();
    void pump();
    const timer = window.setInterval(() => void pump(), 1500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [options.scopeKey, options.api, publish]);

  useLayoutEffect(() => {
    if (!options.scopeKey) return;
    try {
      publish(key);
    } catch {
      current.current.onError?.(
        new Error(
          "Your browser could not load queued messages. Please reload this page.",
        ),
      );
    }
  }, [key, options.scopeKey, publish]);

  const enqueue = useCallback(
    (input: {
      userMessage: string;
      fileContentIds?: string[];
      fileReferences?: QueuedChatMessage["fileReferences"];
    }): Promise<void> => {
      try {
        if (!current.current.scopeKey)
          throw new Error("Sign in before sending a message.");
        const ids = input.fileContentIds ?? [];
        if (ids.length > 50 || new Set(ids).size !== ids.length)
          throw new Error("Choose up to 50 different files.");
        if (!input.userMessage.trim() && !ids.length) return Promise.resolve();
        const queueKey = keyRef.current;
        saveQueue(queueKey, [
          ...readQueue(queueKey),
          {
            id: crypto.randomUUID(),
            text: input.userMessage,
            matterId: current.current.matterId,
            fileContentIds: [...ids],
            fileReferences: input.fileReferences,
            status: "processing",
          },
        ]);
        publish(queueKey);
        trigger.current();
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(
          error instanceof Error
            ? error
            : new Error("The message could not be queued. Please retry."),
        );
      }
    },
    [publish],
  );
  const retry = useCallback(
    (id: string) => {
      const queueKey = keyRef.current;
      saveQueue(
        queueKey,
        readQueue(queueKey).map((message) =>
          message.id === id
            ? { ...message, status: "processing", error: undefined }
            : message,
        ),
      );
      publish(queueKey);
      trigger.current();
    },
    [publish],
  );
  const remove = useCallback(
    (id: string) => {
      const queueKey = keyRef.current;
      saveQueue(
        queueKey,
        readQueue(queueKey).filter(
          (message) => message.id !== id || message.status === "sending",
        ),
      );
      publish(queueKey);
      trigger.current();
    },
    [publish],
  );
  // Retain accepted bubbles until the display projection confirms their exact
  // request IDs. Never infer delivery from matching text or another run's state.
  useEffect(() => {
    if (!options.confirmedRequestIds?.length || snapshot.key !== key) return;
    const confirmed = new Set(options.confirmedRequestIds);
    try {
      const saved = readQueue(key);
      const remaining = saved.filter((message) => !confirmed.has(message.id));
      if (remaining.length !== saved.length) saveQueue(key, remaining);
    } catch {
      // A storage failure must not take the saved conversation offscreen.
      current.current.onError?.(
        new Error(
          "Your browser could not update queued messages. Please reload this page.",
        ),
      );
    }
  }, [key, options.confirmedRequestIds, snapshot]);
  const queuedMessages = useMemo(() => {
    if (snapshot.key === key) return snapshot.messages;
    // Promotion writes the destination queue before changing the thread ID.
    // Read that exact queue so the optimistic bubble survives this commit.
    try {
      return readQueue(key);
    } catch {
      // The layout effect reports storage errors for the selected queue.
      return [];
    }
  }, [key, snapshot]);
  return {
    queuedMessages,
    enqueue,
    retry,
    remove,
    isSending: queuedMessages.some((message) => message.status === "sending"),
  };
}
